/**
 * Which of a person's site rings are lit (JIM-0919-08/13).
 *
 * Jim 2026-09-19: Jarod's map showed 425 sites while People Hub said 35 —
 * the map used every access path, the Hub counted assigned-inspector only.
 * One rule now, three rings: `inspecting` (assigned inspector, or delegated
 * to them), `consulting` (consultant of record), `reachable` (their groups
 * and their org). Jim 2026-09-21: "buttons for show inspections, show
 * consulting — each populates on its own, both can be on at the same time,
 * as well as active and all." Each ring is a button; the lit set goes to the
 * API as `rings=`.
 */

export type Ring = 'inspecting' | 'consulting' | 'reachable';

export const RINGS: readonly Ring[] = ['inspecting', 'consulting', 'reachable'];

export const RING_LABELS: Record<Ring, string> = {
  inspecting: 'Show inspections',
  consulting: 'Show consulting',
  reachable: 'Show group sites',
};

export type RingCount = { total: number; active: number };

export type ScopeCounts = {
  rings: {
    inspecting: RingCount;
    delegated: RingCount;
    consulting: RingCount;
    groups: RingCount;
    org: RingCount;
  };
  mine: RingCount;
  all: RingCount;
  scope_default: 'mine' | 'all';
};

export type CountMode = 'active' | 'total';

const ADMIN_ROLES = new Set(['admin', 'super_admin']);

export function isAdminUser(
  user: { role?: string | null; secondary_roles?: string[] | null } | null
): boolean {
  if (!user) return false;
  const roles = [user.role ?? '', ...(user.secondary_roles ?? [])];
  return roles.some((r) => ADMIN_ROLES.has(r));
}

/**
 * What lights up first: inspections and consulting. Someone with nothing in
 * either ring (a brand-new consultant, say) gets group sites lit instead so
 * the page is not empty.
 */
export function defaultRings(counts?: ScopeCounts): Ring[] {
  if (counts && counts.mine.total === 0 && counts.all.total > 0) {
    return ['reachable'];
  }
  return ['inspecting', 'consulting'];
}

/** The number on a ring's button — working sites or everything on file. */
export function ringCount(
  counts: ScopeCounts | undefined,
  ring: Ring,
  mode: CountMode
): number | null {
  if (!counts) return null;
  switch (ring) {
    case 'inspecting':
      return counts.rings.inspecting[mode] + counts.rings.delegated[mode];
    case 'consulting':
      return counts.rings.consulting[mode];
    case 'reachable':
      return Math.max(counts.all[mode] - counts.mine[mode], 0);
  }
}

export function toggleRing(rings: readonly Ring[], ring: Ring): Ring[] {
  return rings.includes(ring)
    ? rings.filter((r) => r !== ring)
    : RINGS.filter((r) => r === ring || rings.includes(r));
}

/** Stable query value: rings in canonical order, "" when none are lit. */
export function ringsParam(rings: readonly Ring[]): string {
  return RINGS.filter((r) => rings.includes(r)).join(',');
}
